import { useEffect, useState } from "react";
import {
  DndContext, closestCorners, PointerSensor, KeyboardSensor,
  useSensor, useSensors, DragOverlay, useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, RotateCcw, Search } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { V } from "@/lib/vocab";

const clone = (obj) => {
  const out = {};
  for (const k of Object.keys(obj)) out[k] = [...obj[k]];
  return out;
};

function SortableRow({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.to });
  const Icon = item.icon;
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`customize-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex items-center gap-2 rounded-lg border border-sand-200 bg-white px-2.5 py-2 shadow-sm">
      <button {...attributes} {...listeners} type="button" aria-label={`Drag ${item.label}`}
        className="cursor-grab active:cursor-grabbing text-ink/30 hover:text-ink/70 touch-none shrink-0">
        <GripVertical className="h-4 w-4" />
      </button>
      {Icon && <Icon className="h-4 w-4 text-ink/50 shrink-0" strokeWidth={1.5} />}
      <span className="text-sm text-ink truncate">{item.label}</span>
    </div>
  );
}

function Column({ id, items }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wide text-ink/40 mb-1.5">{id}</div>
      <SortableContext id={id} items={items.map((i) => i.to)} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} data-testid={`customize-col-${id.toLowerCase().replace(/\s+/g, "-")}`}
          className={`min-h-[52px] space-y-1.5 rounded-xl border-2 border-dashed p-2 transition-colors ${isOver ? "border-redline-400 bg-redline-50/60" : "border-sand-200 bg-sand-50/50"}`}>
          {items.length === 0 && <div className="px-1 py-2 text-xs text-ink/30">Drag items here</div>}
          {items.map((it) => <SortableRow key={it.to} item={it} />)}
        </div>
      </SortableContext>
    </div>
  );
}

const PRESET_COPY = {
  classic: { name: "Classic", blurb: "Daily work · Community tools · Community · Records · Settings — the menu every community has had so far." },
  v2: { name: "New", blurb: "Inbox · Operations · Communications · Knowledge · Records · Settings — grouped by why you open a page." },
};

// Drag-to-organize the sidebar. Groups (sections) stay fixed; items reorder within a section
// and move between sections. Saves a shared layout for the whole console.
export const CustomizeMenuDialog = ({ open, onOpenChange, groups, initialItems, defaultItems, onSave, saving,
  preset = null, presetSource = "default", groupsFor, defaultsFor, isSuper = false, platformDefault = null, onPlatformDefault, quickSearchOn = false }) => {
  const [cols, setCols] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [style, setStyle] = useState(preset || "classic");
  const [quickSearch, setQuickSearch] = useState(!!quickSearchOn);
  const activeGroups = preset && groupsFor ? groupsFor(style) : groups;

  // Seed only when the dialog opens so in-progress edits aren't wiped by parent re-renders.
  useEffect(() => {
    if (open) { setCols(clone(initialItems)); setActiveId(null); setStyle(preset || "classic"); setQuickSearch(!!quickSearchOn); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickStyle = (p) => {
    if (p === style) return;
    setStyle(p);
    setCols(clone(p === preset ? initialItems : (defaultsFor ? defaultsFor(p) : defaultItems)));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const findContainer = (id) => {
    if (id in cols) return id;
    return Object.keys(cols).find((g) => cols[g].some((it) => it.to === id));
  };

  const activeItem = activeId
    ? Object.values(cols).flat().find((it) => it.to === activeId)
    : null;

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id);
    if (!from || !to) return;
    setCols((prev) => {
      if (from === to) {
        const arr = [...prev[from]];
        const oldIndex = arr.findIndex((i) => i.to === active.id);
        let newIndex = (over.id in prev) ? arr.length - 1 : arr.findIndex((i) => i.to === over.id);
        if (newIndex === -1) newIndex = arr.length - 1;
        if (oldIndex === newIndex) return prev;
        return { ...prev, [from]: arrayMove(arr, oldIndex, newIndex) };
      }
      const fromArr = [...prev[from]];
      const idx = fromArr.findIndex((i) => i.to === active.id);
      if (idx === -1) return prev;
      const [moved] = fromArr.splice(idx, 1);
      const toArr = [...prev[to]];
      let insertAt = (over.id in prev) ? toArr.length : toArr.findIndex((i) => i.to === over.id);
      if (insertAt === -1) insertAt = toArr.length;
      toArr.splice(insertAt, 0, moved);
      return { ...prev, [from]: fromArr, [to]: toArr };
    });
  };

  const save = () => {
    const layout = { groups: activeGroups.map((gid) => ({ id: gid, items: (cols[gid] || []).map((i) => i.to) })), quick_search: quickSearch };
    if (preset) layout.preset = style;
    onSave(layout);
  };
  const resetDefaults = () => setCols(clone(preset && defaultsFor ? defaultsFor(style) : defaultItems));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-lg max-h-[92dvh] overflow-y-auto overflow-x-hidden overscroll-contain p-4 sm:p-6" data-testid="customize-menu-dialog">
        <DialogHeader><DialogTitle className="font-heading">Organize menu</DialogTitle></DialogHeader>
        <p className="-mt-1 text-sm text-muted-foreground">Drag items to reorder them or move them between sections. This sets the menu for everyone in this console.</p>

        {preset && (
          <div data-testid="menu-style" className="rounded-xl border border-sand-200 bg-sand-50/60 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-ink/45">Menu style</span>
              <span className="inline-flex rounded-full border border-sand-200 bg-white p-0.5">
                {["classic", "v2"].map((p) => (
                  <button key={p} type="button" data-testid={`menu-style-${p}`} onClick={() => pickStyle(p)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${style === p ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>
                    {PRESET_COPY[p].name}
                  </button>
                ))}
              </span>
            </div>
            <p className="mt-1.5 text-xs text-ink/60">{PRESET_COPY[style].blurb}</p>
            {style !== preset && <p data-testid="menu-style-hint" className="mt-1 text-xs font-semibold text-amber-700">Switching style starts from that style's default layout. Nothing is removed — every page keeps its place in a section.</p>}
            {style === preset && presetSource !== "community" && (
              <p className="mt-1 text-[11px] text-ink/45">Currently following the {presetSource === "platform" ? "platform default" : "product default"} — saving makes it this {V.lc("org")}'s own choice, so a later platform change won't move it.</p>
            )}
            {isSuper && onPlatformDefault && (
              <div data-testid="platform-nav-default" className="mt-2 flex flex-wrap items-center gap-2 border-t border-sand-200 pt-2 text-[11px] text-ink/60">
                <span className="font-semibold">Platform default</span>
                <span className="text-ink/45">({V.lcs("org")} without their own choice)</span>
                <span className="ml-auto inline-flex rounded-full border border-sand-200 bg-white p-0.5">
                  {[["classic", "Classic"], ["v2", "New"]].map(([p, name]) => (
                    <button key={p} type="button" data-testid={`platform-nav-default-${p}`} onClick={() => onPlatformDefault(p)}
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${(platformDefault || "v2") === p ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>{name}</button>
                  ))}
                </span>
              </div>
            )}
          </div>
        )}

        <div data-testid="quick-search-setting" className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-ink"><Search className="h-3.5 w-3.5 text-ink/60" strokeWidth={2} /> Quick search (⌘K)</div>
            <p className="mt-0.5 text-xs text-ink/60">A search box in the header — jump to any page, or find {V.a("person")}, request, conversation, document or vendor by name, phone or unit. Off by default; when off there's no box and no ⌘K shortcut.</p>
          </div>
          <Switch data-testid="quick-search-toggle" checked={quickSearch} onCheckedChange={setQuickSearch} className="mt-0.5 shrink-0" />
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCorners}
          onDragStart={({ active }) => setActiveId(active.id)}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}>
          <div className="mt-3 space-y-4">
            {activeGroups.map((g) => <Column key={g} id={g} items={cols[g] || []} />)}
          </div>
          <DragOverlay>
            {activeItem ? (
              <div className="flex items-center gap-2 rounded-lg border border-redline-300 bg-white px-2.5 py-2 shadow-lg">
                <GripVertical className="h-4 w-4 text-ink/40" />
                {activeItem.icon && <activeItem.icon className="h-4 w-4 text-ink/50" strokeWidth={1.5} />}
                <span className="text-sm text-ink">{activeItem.label}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button type="button" data-testid="customize-reset-btn" onClick={resetDefaults}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-ink transition-colors">
            <RotateCcw className="h-4 w-4" /> Reset to default
          </button>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="customize-cancel-btn" onClick={() => onOpenChange(false)}
              className="rounded-full px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-sand-100 transition-colors">
              Cancel
            </button>
            <button type="button" data-testid="customize-save-btn" onClick={save} disabled={saving}
              className="rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white hover:bg-ink-700 transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Save menu"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
